import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Users, Mail, Phone } from 'lucide-react';
import SectionHeading from '../components/SectionHeading';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultBoardMembers = [
  {
    name: 'Hassan Syed',
    title: 'President',
    role: 'Executive Board',
  },
  {
    name: 'Bashir Hassan',
    title: 'Vice President',
    role: 'Executive Board',
  },
  {
    name: 'Muhammad Aboyeji',
    title: 'Treasurer',
    role: 'Executive Board',
  },
  {
    name: 'Maryam Yusuf',
    title: 'Secretary',
    role: 'Executive Board',
  },
  {
    name: 'Imam Abdul Halick Bawa',
    title: 'Director of Religious Affairs',
    role: 'Religious Leadership',
  },
  {
    name: 'Balkisu Aliyu',
    title: 'Director of Women Affairs',
    role: 'Coordinators',
  },
  {
    name: 'Zishan Ahmad',
    title: 'Male Coordinator',
    role: 'Coordinators',
  },
  {
    name: 'Aysha Hassan',
    title: 'Female Coordinator 1',
    role: 'Coordinators',
  },
  {
    name: 'Aisha Aliyu',
    title: 'Female Coordinator 2',
    role: 'Coordinators',
  },
  {
    name: 'Yahya Muhammad',
    title: 'IT Technician',
    role: 'Support',
  },
];

const BoardPage = () => {
  const [boardMembers, setBoardMembers] = useState(defaultBoardMembers);

  useEffect(() => {
    axios.get(`${API}/content/board`)
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length > 0) {
          setBoardMembers(res.data);
        }
      })
      .catch(() => {/* fall back */});
  }, []);

  return (
    <div className="min-h-screen pt-24">
      {/* Hero */}
      <section className="py-16 lg:py-24 hero-gradient islamic-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              Our Leadership
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground font-['Outfit'] leading-tight mb-6">
              Meet the <span className="text-gradient">Board</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Our dedicated team of volunteers serving the True North Muslim Community.
              All officers and coordinators serve in volunteer capacities.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Executive Board */}
      <section className="py-16 lg:py-24" data-testid="executive-board">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="Executive Board"
            title="Leadership Team"
            description="The executive team guiding our community's vision and operations"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {boardMembers.filter(m => m.role === 'Executive Board').map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-card border border-border hover:border-primary/50 transition-all text-center"
                data-testid={`board-member-${index}`}
              >
                {/* Avatar placeholder */}
                <div className="w-24 h-24 rounded-full bg-primary/10 border-2 border-primary/30 mx-auto mb-4 flex items-center justify-center">
                  <Users className="h-10 w-10 text-primary/50" />
                </div>
                <h3 className="text-lg font-semibold text-foreground font-['Outfit']">{member.name}</h3>
                <p className="text-primary font-medium">{member.title}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Religious Leadership */}
      <section className="py-16 lg:py-24 bg-card border-y border-border" data-testid="religious-leadership">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="Religious Leadership"
            title="Spiritual Guidance"
            description="Our religious leader providing Islamic guidance and education"
          />
          
          <div className="max-w-md mx-auto">
            {boardMembers.filter(m => m.role === 'Religious Leadership').map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="p-8 rounded-2xl bg-background border border-secondary/30 text-center"
              >
                {/* Avatar placeholder */}
                <div className="w-32 h-32 rounded-full bg-secondary/10 border-2 border-secondary/30 mx-auto mb-4 flex items-center justify-center">
                  <Users className="h-12 w-12 text-secondary/50" />
                </div>
                <h3 className="text-xl font-semibold text-foreground font-['Outfit']">{member.name}</h3>
                <p className="text-secondary font-medium text-lg">{member.title}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Coordinators */}
      <section className="py-16 lg:py-24" data-testid="coordinators">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="Coordinators"
            title="Community Coordinators"
            description="Dedicated volunteers coordinating community activities and programs"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {boardMembers.filter(m => m.role === 'Coordinators').map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-card border border-border hover:border-primary/50 transition-all text-center"
              >
                {/* Avatar placeholder */}
                <div className="w-24 h-24 rounded-full bg-primary/10 border-2 border-primary/30 mx-auto mb-4 flex items-center justify-center">
                  <Users className="h-10 w-10 text-primary/50" />
                </div>
                <h3 className="text-lg font-semibold text-foreground font-['Outfit']">{member.name}</h3>
                <p className="text-primary font-medium">{member.title}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Support Team */}
      <section className="py-16 lg:py-24 bg-card border-t border-border" data-testid="support-team">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="Support"
            title="Support Team"
            description="Technical and administrative support for our community"
          />
          
          <div className="max-w-md mx-auto">
            {boardMembers.filter(m => m.role === 'Support').map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="p-6 rounded-2xl bg-background border border-border text-center"
              >
                {/* Avatar placeholder */}
                <div className="w-24 h-24 rounded-full bg-primary/10 border-2 border-primary/30 mx-auto mb-4 flex items-center justify-center">
                  <Users className="h-10 w-10 text-primary/50" />
                </div>
                <h3 className="text-lg font-semibold text-foreground font-['Outfit']">{member.name}</h3>
                <p className="text-primary font-medium">{member.title}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-foreground mb-4">
            Get in Touch with Our Team
          </h2>
          <p className="text-muted-foreground mb-8">
            Have questions or want to get involved? Reach out to us!
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="mailto:truenorthmuslim@gmail.com" className="flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
              <Mail className="h-5 w-5" />
              truenorthmuslim@gmail.com
            </a>
            <a href="tel:249-444-5227" className="flex items-center gap-2 px-6 py-3 rounded-full bg-card border border-border text-foreground hover:border-primary transition-colors">
              <Phone className="h-5 w-5" />
              249-444-5227
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BoardPage;
